import { useCallback, useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Receipt,
  PlusCircle,
  RotateCw,
  FileSpreadsheet,
  Clock,
  CircleCheck,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import CommonButton from '../../../components/common-components/button/CommonButton';
import CommonDropdown, {
  Option,
} from '../../../components/common-components/dropdown/CommonDropdown';
import { useStore } from '../../../hooks/useStore';
import type { IExpenseResult } from '../../../models/expense/ExpenseModel';
import type { IUserManagementResult } from '../../../models/user-management/UserManagementModel';
import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import gridStyles from '../../../styles/ODataGridPage.module.css';
import { formatDecimalAmount } from '../../../utils/numberUtils';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import { getApprovalStatusRowClassName } from '../../../utils/gridRowClassNames';
import { useExpenseManagementColumns } from './ExpenseManagement.grid';
import styles from './ExpenseManagement.module.css';
import EditExpensePopup from './parts/EditExpensePopup';
import ExpenseDetailPopup from './parts/ExpenseDetailPopup';
import NewExpensePopup from './parts/NewExpensePopup';
import DeleteExpenseDialog from './parts/DeleteExpenseDialog';

const GRID_ID = 'expense-management-grid';

const ExpenseManagement = () => {
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const { popupStore, expenseStore, authStore } = useStore();
  const { pendingTotal, approvedTotal, selectedUserId } = expenseStore;

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      if (authStore.isAdmin) {
        try {
          const usersList = await userManagementService.getAllUsers();
          const actualUsers = Array.isArray(usersList)
            ? usersList
            : (usersList as any)?.value || [];
          setUsers(actualUsers);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }
    };

    void loadUsers();
  }, [authStore.isAdmin]);

  const isMobile = viewportWidth < 768;
  const pageSize = isMobile ? 10 : 20;
  const showAdminColumns = authStore.hasAnyRole(['Admin']);
  const columns = useExpenseManagementColumns(showAdminColumns);

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IExpenseResult>>(
    () => ({
      gridId: GRID_ID,
      rows: pageSize,
      oDataMapping,
      fetchRows: async params => {
        const result = await expenseStore.listExpenses(buildLegacyGridParams(params, { oDataMapping }));
        return { rows: result.rows, totalRecords: result.lastRow };
      },
    }),
    [expenseStore, pageSize, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IExpenseResult>) => {
      expenseStore.setGridHelpers(helpers);
      void expenseStore.fetchExpenseTotals();
    },
    [expenseStore]
  );

  const exportToExcel = useCallback(
    async (helpers: GridToolbarHelpers<IExpenseResult>) => {
      try {
        const queryState = helpers.getQueryState({ skip: 0, top: 1000000 });
        const result = await expenseStore.listExpenses(buildLegacyGridParams(queryState));

        if (!result?.rows.length) {
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id,
          Kullanıcı: item.userName,
          Miktar: item.amount,
          'Harcama Tarihi': new Date(item.expenseDate).toLocaleDateString('tr-TR'),
          Açıklama: item.description,
          Durum:
            item.status === 'Pending'
              ? 'Beklemede'
              : item.status === 'Approved'
                ? 'Onaylandı'
                : 'Reddedildi',
          'Oluşturulma Tarihi': new Date(item.createdDate).toLocaleDateString('tr-TR'),
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Harcama Kayitlari');
        XLSX.writeFile(wb, `Harcama_Kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
        console.error('Excel export error:', error);
      }
    },
    [expenseStore]
  );

  const handleUserChange = async (userId: string | null) => {
    const selectedId = userId === 'all' || !userId ? null : userId;
    expenseStore.setSelectedUserId(selectedId);
    await expenseStore.fetchExpenseTotals(selectedId);
    expenseStore.refreshGrid();
  };

  const handleRefresh = async (helpers: GridToolbarHelpers<IExpenseResult>) => {
    await expenseStore.fetchExpenseTotals();
    helpers.refreshGrid();
  };

  const getRowClassName = (row: IExpenseResult) => getApprovalStatusRowClassName(row.status);

  const userOptions: Option<string>[] = useMemo(() => {
    const baseOptions: Option<string>[] = [{ value: 'all', label: 'Tüm Kullanıcılar' }];

    if (Array.isArray(users) && users.length > 0) {
      const mappedUserOptions: Option<string>[] = users.map(user => ({
        value: user.id,
        label: `${user.userName}${
          user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''
        }`,
      }));
      return [...baseOptions, ...mappedUserOptions];
    }

    return baseOptions;
  }, [users]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerInner}>
            <div className={styles.titleRow}>
              <span className={styles.titleIcon} aria-hidden="true">
                <Receipt size={20} strokeWidth={2} />
              </span>
              <div className={styles.titleContent}>
                <h2 className={styles.mainTitle}>Harcama Yönetimi</h2>
                <p className={styles.pageDescription}>
                  Harcama kayıtlarını oluşturun, takip edin ve yönetin.
                </p>
              </div>
            </div>

            <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statCardHeader}>
                <span className={`${styles.statIcon} ${styles.statIconPending}`} aria-hidden="true">
                  <Clock size={15} strokeWidth={2} />
                </span>
                <span className={styles.statLabel}>Beklemede Toplam</span>
              </div>
              <p className={`${styles.statValue} ${styles.statValuePending}`}>
                {formatDecimalAmount(pendingTotal)} ₺
              </p>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statCardHeader}>
                <span className={`${styles.statIcon} ${styles.statIconApproved}`} aria-hidden="true">
                  <CircleCheck size={15} strokeWidth={2} />
                </span>
                <span className={styles.statLabel}>Onaylandı Toplam</span>
              </div>
              <p className={`${styles.statValue} ${styles.statValueApproved}`}>
                {formatDecimalAmount(approvedTotal)} ₺
              </p>
            </div>
            </div>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <ODataGrid<IExpenseResult>
            gridId={GRID_ID}
            config={gridConfig}
            columns={columns}
            dataKey="id"
            wrapperClassName={gridStyles.oDataGridPage}
            resizableColumns
            onGridReady={onGridReady}
            rowClassName={getRowClassName}
            toolbarLeft={helpers => (
              <>
                <CommonButton
                  type="primary"
                  onClick={() => popupStore.setIsNewExpenseOpen(true)}
                  leftIcon={<PlusCircle size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnExpense}
                >
                  Yeni Harcama Kaydı
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => void exportToExcel(helpers)}
                  leftIcon={<FileSpreadsheet size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={() => void handleRefresh(helpers)}
                  leftIcon={<RotateCw size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            toolbarRight={
              authStore.isAdmin ? (
                <div className={styles.userSelector}>
                  <CommonDropdown
                    options={userOptions}
                    value={selectedUserId ?? 'all'}
                    onChange={handleUserChange}
                    label="Kullanıcı Seçin"
                    className={styles.dropdown}
                  />
                </div>
              ) : null
            }
          />
        </div>
      </div>

      {popupStore.isNewExpenseOpen && <NewExpensePopup />}
      {popupStore.isEditExpenseOpen && <EditExpensePopup />}
      {popupStore.isExpenseDetailOpen && <ExpenseDetailPopup />}
      <DeleteExpenseDialog />
    </>
  );
};

export default observer(ExpenseManagement);
